/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import { useState, useEffect, useMemo } from 'react';
import { useMount } from 'react-use';
import { v4 as uuidv4 } from 'uuid';
import { useLocation, useHistory } from 'react-router-dom';
import { TableQueryService } from 'shared/navigation/TableQueryService';
import { jsDateToLocalISO8601DateString } from 'shared/utils/dateUtils';
import { useTranslation } from 'react-i18next';
import useProcedure from '../../../procedure/useProcedure';
import { InputType } from './FilterType';
import GenericCellDataAdapter from '../adapters/GenericCellDataAdapter';
import { SubmissionStatus } from '../../../models/SubmissionStatus';
import { useConfiguration } from '../../../redux/features/configuration/useConfiguration';
import { ConfigResourceProps } from '../../../redux/enums/ConfigResourceProps';
import { isFeatureEnabled, ToggleableFeature } from '../../../shared/utils/featuresUtils';

function sanitizeFilters(clientSideFilters) {
  return clientSideFilters.map((f) => {
    const r = {};
    r.type = f.type;
    r.inputType = f.inputType;
    r.value = f.value;
    return r;
  });
}

export default function useTableFilter(filter, setFilter, updatePage, columnsConfiguration) {
  const location = useLocation();
  const procedure = useProcedure();
  const history = useHistory();
  const [applicableFilters, setApplicableFilters] = useState([]);
  const [showFilterDetails, setShowFilterDetails] = useState(false);
  const tableQueryService = new TableQueryService(history, location);
  const [filters, setFilters] = useState([]);
  const { t } = useTranslation();
  const globalConfiguration = useConfiguration(ConfigResourceProps.GLOBAL_CONFIGURATION);

  useMount(() => {
    // TODO: "filter" needs to be changed ASAP to another name, as location.state has a prototype named "filter"
    // This causes problems when you are coming back from a route which has a state but it doesn't have "filter" defined
    // js will try to map over the "filter" function of location.state
    if (location?.state?.filter) {
      const filtersWithId = location.state.filter.map(filterElement => ({
        id: uuidv4(),
        ...filterElement,
      }));
      setFilters(filtersWithId);
      setShowFilterDetails(true);
    }
    if (tableQueryService?.filter?.length > 0) {
      setShowFilterDetails(true);
    }
  });

  useEffect(() => {
    if (filters.length && !filter?.length) {
      clearFilters();
    }
  }, [filter]);

  useEffect(() => {
    if (filter?.length > 0) {
      setFilters(enrichFilters(filter));
    }
  }, [filter]);

  useEffect(() => {
    setShowFilterDetails(tableQueryService?.filter?.length > 0);
    clearFilters();
  }, [procedure?.id]);

  useEffect(() => {
    if (columnsConfiguration?.ready) {
      const genericCellDataAdapter = GenericCellDataAdapter(columnsConfiguration?.resource, null, null, null, null, t);
      const filterableColumns = genericCellDataAdapter
        ?.filter(column => column.filterId)
        ?.flatMap((column) => {
          return column.filterIds.map((filterId, index) => ({
            label: column.labels[index],
            testId: `${filterId}TestId`,
            className: 'filterField',
            type: filterId,
            inputType: column.filterType,
            statuses: column?.statuses,
          }));
        });
      setApplicableFilters(filterableColumns);
    }
  }, [procedure?.id, columnsConfiguration?.ready]);

  function clearFilters() {
    setFilters([]);
    setFilter([]);
    updatePage(0);
  }

  function clearFilterAction() {
    tableQueryService.setFilter([]);
    setFilters([]);
    setFilter([]);
    updatePage(0);
  }

  const deleteFilter = (filterElement) => {
    const id = filterElement?.id;
    const newList = id
      ? filters.filter(filterItem => filterItem.id !== id)
      : filters.filter(_filter => !(_filter.type === filterElement.type && _filter.value === filterElement.value));
    tableQueryService.setFilter(newList);
    setFilters(newList);
    setFilter(sanitizeFilters(newList));
    updatePage(0);
  };

  const enterHandler = (event, type) => {
    if (event.key === 'Enter') {
      updatePage(0);
      if (
        filters.filter(filterItem => filterItem.type === type && filterItem.value === event.target.value.trim())
          .length === 0 &&
        event.target.value.trim() !== ''
      ) {
        const filterList = filters;
        filterList.push({
          id: uuidv4(),
          value: event.target.value,
          inputType: InputType.TEXT_FIELD,
          type,
        });
        setFilters(filterList);
        setFilter(sanitizeFilters(filterList));
        event.target.value = '';
      }
    }
  };

  function addDateFilter(e, type) {
    const shortDate = jsDateToLocalISO8601DateString(e);
    if (!shortDate) return;

    if (filters.filter(filterItem => filterItem.type === type && filterItem.value === shortDate).length === 0) {
      const filterList = filters;
      filterList.push({
        id: uuidv4(),
        type,
        inputType: InputType.DATE_PICKER,
        value: shortDate,
      });
      setFilters(filterList);
      setFilter(sanitizeFilters(filterList));
      updatePage(0);
    }
  }

  function addStatusFilter(e, type) {
    if (
      e.target.value !== 0 &&
      filters.filter(filterItem => filterItem.type === type && filterItem.value === e.target.value).length === 0
    ) {
      const filterList = filters;
      filterList.push({
        id: uuidv4(),
        type,
        inputType: InputType.STATUS,
        value: e.target.value,
      });
      updatePage(0);
      setFilters(filterList);
      setFilter(sanitizeFilters(filters));
    }
  }

  function addCustomFilter(e, type) {
    if (
      e.target.value !== 0 &&
      filters.filter(filterItem => filterItem.type === type && filterItem.value === e.target.value).length === 0
    ) {
      const filterList = filters;
      filterList.push({
        id: uuidv4(),
        type,
        value: e.target.value,
      });
      updatePage(0);
      setFilters(filterList);
      setFilter(sanitizeFilters(filters));
    }
  }

  function addRolesFilter(e, type) {
    if (
      e.target.value !== 0 &&
      filters.filter(filterItem => filterItem.type === type && filterItem.value === e.target.value).length === 0
    ) {
      const filterList = filters;
      filterList.push({
        id: uuidv4(),
        type,
        inputType: InputType.ROLES,
        value: e.target.value,
      });
      updatePage(0);
      setFilters(filterList);
      setFilter(sanitizeFilters(filters));
    }
  }

  function enrichFilters(_filter) {
    return _filter?.map((f) => {
      if (!f.id) {
        f.id = uuidv4();
      }
      return f;
    });
  }

  const toggleFilterDetails = () => {
    setShowFilterDetails(prev => !prev);
  };

  // updates the PAYMENT status valus as visible if feature is toggled
  const statusValues = useMemo(() => {
    if (isFeatureEnabled(globalConfiguration.resource, ToggleableFeature.PAYMENT)) {
      SubmissionStatus.READY_TO_PAY.visible = true;
      SubmissionStatus.RESIGNING_FAILED.visible = true;
    }

    return SubmissionStatus;
  }, [globalConfiguration.ready]);

  return {
    applicableFilters,
    addDateFilter,
    addRolesFilter,
    addStatusFilter,
    clearFilters,
    clearFilterAction,
    deleteFilter,
    enterHandler,
    filters,
    setFilters,
    showFilterDetails,
    toggleFilterDetails,
    statusValues,
    addCustomFilter,
  };
}
